'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, type ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { SiteId } from '@sizzlenz/shared';
import { useSession } from '@/lib/auth-client';
import { HomeSparkSection, ExploreHubsSection, ExploreThemesSection, ExploreAsksSection, ConsumerDiscoverSection, HostAndEventsSection, ExploreToolsSection, OfficeHoursSection, ExportSection, DemandExportHeroSection, DemandToBusinessSection, DemandTeaser, DemandFeedSection, MoneySection } from '.';
import { FinalCTASectionV3 } from '../HomeV3';
import { SignInEmailBanner } from '../auth/SignInEmailBanner';
import { DirectToRightPlaceV4 } from './DirectToRightPlaceV4';
import { HeroSectionSizzleNZ } from './HeroSectionSizzleNZ';
import { LazySection } from './LazySection';
import { FeaturedNewsSection } from './FeaturedNewsSection';
import { useSiteSettings } from '@/lib/hooks/useSiteSettings';
import { HeroSectionSizzleCPG } from './HeroSectionSizzleCPG';
function BusinessMarketplaceHeading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="container mx-auto px-4 py-8 text-center sm:px-6 lg:px-8"><h2 className="mx-auto max-w-3xl text-xl font-extrabold leading-tight text-gray-900">Sizzle is built for brands, trade businesses, agencies, providers, and hosts to be inspired, discover, and connect with opportunities.</h2></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
interface HomeSection {
  key: string;
  content: ReactNode;
  background: string;
  isSignInBanner?: boolean;
  /** If true, section is lazy-loaded with a skeleton placeholder. */
  lazy?: boolean;
  /** Space reserved while a lazy section approaches the viewport. */
  lazyMinHeight?: string;
  /** If set, section only renders on the listed domains. Omit to show on all domains. */
  sites?: SiteId[];
}
const SECTIONS: HomeSection[] = [{
  key: 'hero-nz',
  content: <HeroSectionSizzleNZ />,
  background: 'bg-[#fffaf4]',
  sites: ['sizzlenz']
}, {
  key: 'hero-cpg',
  content: <HeroSectionSizzleCPG />,
  background: 'bg-white',
  sites: ['sizzlecpg']
},
// {
//   key: 'demand-to-business',
//   content: <DemandToBusinessSection />,
//   background: 'bg-white',
//   sites: ['sizzlenz'],
// },
{
  key: 'demand',
  content: <DemandTeaser />,
  background: 'bg-gray-950',
  sites: ['sizzlenz']
}, {
  key: 'demand-feed',
  content: <DemandFeedSection />,
  background: 'bg-white',
  sites: ['sizzlenz']
},
// { key: 'business-marketplace-heading', content: <BusinessMarketplaceHeading /> },
// { key: 'shared-moments', content: <SharedMomentsSection />, background: 'bg-transparent' },
// {
//   key: 'direct-to-right-place',
//   content: <DirectToRightPlaceV4 />,
//   background: 'bg-gray-900',
// },
// {
//   key: 'demand-hero',
//   content: <DemandExportHeroSection />,
//   background: 'bg-gray-950',
//   sites: ['sizzlenz'],
// },
// { key: 'demand', content: <DemandSection />, background: 'bg-white', lazy: true },
// {
//   key: 'export',
//   content: <ExportSection showSpecificScenarios={false} />,
//   background: 'bg-gray-50',
//   lazy: true,
// },
{
  key: 'explore-tools',
  content: <ExploreToolsSection />,
  background: 'bg-[#fffaf4]',
  lazy: true,
  lazyMinHeight: 'clamp(32rem, 48vw, 46rem)'
}, {
  key: 'banner-early-preview',
  isSignInBanner: true,
  background: 'bg-white',
  content: <SignInEmailBanner heading="Join Sizzle as an Early Insider" subtext="Get practical demand-building tools, ideas, opportunities, and connections for your brand or trade business." buttonLabel="Get Early Access" className="py-10" />
}, {
  key: 'explore-hubs',
  content: <ExploreHubsSection />,
  background: 'bg-[#fffaf4]',
  lazy: true,
  lazyMinHeight: 'clamp(30rem, 45vw, 42rem)'
},
// { key: 'money', content: <MoneySection />, background: 'bg-gray-50', lazy: true },
{
  key: 'office-hours',
  content: <OfficeHoursSection />,
  background: 'bg-gray-50',
  lazy: true,
  lazyMinHeight: 'clamp(30rem, 45vw, 42rem)',
  sites: ['sizzlenz']
}, {
  key: 'featured-news',
  content: <FeaturedNewsSection />,
  background: 'bg-[#fffaf4]',
  lazy: true,
  lazyMinHeight: 'clamp(24rem, 36vw, 34rem)'
},
// { key: 'explore-themes', content: <ExploreThemesSection />, background: 'bg-white', lazy: true },
// {
//   key: 'banner-get-started',
//   isSignInBanner: true,
//   background: 'bg-white',
//   lazy: true,
//   content: (
//     <SignInEmailBanner
//       heading="Ready to get started?"
//       subtext="Whether you're here to discover great products with friends or grow your brand, sign up and start making things happen."
//       buttonLabel="Sign Up"
//       className="py-10"
//     />
//   ),
// },
// { key: 'host-at-your-place', content: <HostAndEventsSection />, background: 'bg-white', lazy: true },
// { key: 'consumer-discover', content: <ConsumerDiscoverSection />, background: 'bg-white', sites: ['sizzlenz'] },
// { key: 'explore-asks', content: <ExploreAsksSection />, background: 'bg-gray-50', lazy: true },
{
  key: 'final-cta',
  content: <FinalCTASectionV3 demandOnly />,
  background: 'bg-white',
  lazy: true,
  lazyMinHeight: 'clamp(38rem, 58vw, 52rem)'
}
// {
//   key: 'banner-dont-miss-out',
//   isSignInBanner: true,
//   background: 'bg-gray-950',
//   lazy: true,
//   content: (
//     <SignInEmailBanner
//       heading="Sign up now to be one of the first Sizzle Early Insiders"
//       buttonLabel="Sign Up"
//       variant="dark"
//       className="py-12"
//     />
//   ),
// },
];
interface Crosslink {
  context: string;
  label: string;
  href: string;
}

/** Cross-link shown at the bottom of the page pointing users to the other domain. */
const CROSSLINKS: Record<SiteId, Crosslink | null> = {
  sizzlenz: {
    context: 'Based in the US?',
    label: 'Visit Sizzle CPG Global',
    href: 'https://sizzlecpg.com'
  },
  sizzlecpg: {
    context: 'Based in NZ?',
    label: 'Visit Sizzle NZ',
    href: 'https://sizzle.nz'
  },
  // Yummmsy has its own frontend app and does not share this home page.
  yummmsy: null
};
export function HomeMainView() {
  const $ = _c(10);
  const {
    data: session
  } = useSession();
  const {
    siteId
  } = useSiteSettings();
  const isLoggedIn = !!session?.user;
  let t0;
  if ($[0] !== isLoggedIn || $[1] !== siteId) {
    t0 = SECTIONS.filter(section => {
      if (section.sites && !section.sites.includes(siteId)) {
        return false;
      }
      if (section.isSignInBanner && isLoggedIn) {
        return false;
      }
      return true;
    });
    $[0] = isLoggedIn;
    $[1] = siteId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const visibleSections = t0;
  const crosslink = CROSSLINKS[siteId];
  let t1;
  if ($[3] !== visibleSections) {
    t1 = visibleSections.map(_temp);
    $[3] = visibleSections;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== crosslink) {
    t2 = false && crosslink && <div className="border-t border-orange-100 bg-[#fffaf4] px-4 py-7 text-center"><p className="text-sm text-gray-600">{crosslink.context}{" "}<a href={crosslink.href} target="_blank" rel="noopener noreferrer" className="inline-flex cursor-pointer items-center gap-1 font-semibold text-primary-600 transition-colors hover:text-primary-700">{crosslink.label}<ArrowUpRight className="h-4 w-4" aria-hidden="true" /></a></p></div>;
    $[5] = crosslink;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="min-h-screen overflow-x-clip bg-white">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp(t0) {
  const {
    key,
    content,
    background,
    lazy,
    lazyMinHeight
  } = t0;
  return <div key={key} className={background} data-home-section={key}>{lazy ? <LazySection minHeight={lazyMinHeight}>{content}</LazySection> : content}</div>;
}